import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { api } from "../lib/api";
import {
  getOwnerProject,
  listOwnerProjects,
  purchasePlans,
  triggerActivation,
  generateDocument,
  downloadDocumentPdf,
  downloadProjectPacket,
  sendOwnerProjectMessage,
} from "../lib/ownerPortal";
import { relativeDate, WaitingOnPill, stageBlocker } from "../lib/ownerUi";
import {
  assetUrl,
  formatInt,
  formatPriceFull,
  formatTypology,
} from "../lib/format";
import {
  ChevronRight,
  ChevronLeft,
  Lock,
  Check,
  FileText,
  Hammer,
  Zap,
  Flame,
  Hash,
  Sparkles,
  Download,
  MessageSquare,
  Calendar,
  ArrowRight,
  RefreshCw,
  Send,
  Circle,
} from "lucide-react";
import BackBar from "../components/BackBar";
import WorkflowStrip from "../components/WorkflowStrip";
import ContractSignaturePad from "../components/ContractSignaturePad";

const STAGE_ORDER = [
  "Selection",
  "Consultation",
  "Purchase",
  "Expedited Review",
  "Build",
  "Construction Inspection",
  "Complete",
];

const DOC_TYPES = [
  { key: "feasibility_report", label: "Project Feasibility Report" },
  { key: "lender_underwriting", label: "Lender Underwriting Summary" },
  { key: "ihcda_guidance", label: "IHCDA HOME Funding Guidance" },
  { key: "rezoning_addendum", label: "Rezoning Petition Addendum" },
];

export default function OwnerProjectDetail() {
  const { projectId } = useParams();
  const { user } = useRole();

  const [p, setP] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [card, setCard] = useState({ card_number: "", expiry: "", cvc: "" });
  const [signature, setSignature] = useState({
    method: "typed",
    value: "",
    typed_name: "",
  });
  const [purchaseErr, setPurchaseErr] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);

  const refresh = async () => {
    try {
      const d = await getOwnerProject(projectId, user.id);
      setP(d);
    } catch (e) {
      setErr(e);
    }
  };
  useEffect(() => {
    if (!user?.id || !projectId) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, projectId]);

  // Fetch all of the owner's projects once so we can render a
  // "Project N of M · prev / next" pager in the header.
  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    listOwnerProjects(user.id)
      .then((d) => {
        if (!alive) return;
        const flat = [
          ...(d?.in_progress || []),
          ...(d?.awaiting_permit || []),
          ...(d?.completed || []),
        ];
        setSiblings(flat);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (err) {
    return (
      <p style={{ color: "#A23B2E" }} data-testid="owner-pd-error">
        Could not load this project.
      </p>
    );
  }
  if (!p) {
    return (
      <div style={{ color: "#6C6B85", fontStyle: "italic" }} data-testid="owner-pd-loading">
        Loading project…
      </div>
    );
  }

  const stageIdx = STAGE_ORDER.indexOf(p.current_stage);
  const sqft = (p.design && p.design.sqft) || 0;
  const planAmount = 2500 + Math.max(0, parseInt(sqft, 10) || 0);

  // ─── Urgency: how long has the project been in its current stage? ───
  // Pulled from stage_history; safely returns null when unknown.
  const currentStageEntry = (p.stage_history || []).find(
    (h) => h.stage === p.current_stage,
  );
  const enteredCurrentStageAt = currentStageEntry?.at || p.updated_at || null;
  const daysInStage = (() => {
    if (!enteredCurrentStageAt) return null;
    const diff = Date.now() - new Date(enteredCurrentStageAt).getTime();
    const d = Math.floor(diff / 86400000);
    return d > 0 ? d : null;
  })();
  const enteredLabel = enteredCurrentStageAt
    ? new Date(enteredCurrentStageAt).toLocaleDateString("en-US", {
        month: "short", day: "numeric",
      })
    : null;
  const blocker = stageBlocker(p.current_stage);
  const ownerBlocked = blocker.tone === "owner";

  // ─── Project pager (siblings) ───
  const sibIndex = siblings.findIndex((s) => s.id === p.id);
  const sibPrev = sibIndex > 0 ? siblings[sibIndex - 1] : null;
  const sibNext =
    sibIndex >= 0 && sibIndex < siblings.length - 1
      ? siblings[sibIndex + 1]
      : null;

  const submitPurchase = async () => {
    setPurchaseErr(null);
    setBusy("purchase");
    try {
      await purchasePlans(projectId, user.id, card, signature);
      setCard({ card_number: "", expiry: "", cvc: "" });
      setSignature({ method: "typed", value: "", typed_name: "" });
      await refresh();
    } catch (e) {
      setPurchaseErr(e?.response?.data?.detail || "Could not complete the purchase.");
    } finally {
      setBusy(null);
    }
  };

  const fireActivation = async (key) => {
    setBusy(`act.${key}`);
    try {
      await triggerActivation(projectId, user.id, key);
      await refresh();
    } finally {
      setBusy(null);
    }
  };

  const generate = async (docType) => {
    setBusy(`doc.${docType}`);
    try {
      const created = await generateDocument(projectId, user.id, docType);
      await refresh();
      if (created) setViewDoc(created);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="animate-fade-in-up" data-testid="owner-project-detail">
      {/* Unified back + breadcrumbs (with the project pager pinned right). */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
        <BackBar
          parent={{ to: "/owner/projects", label: "Projects" }}
          trail={[
            { label: "Owner", to: "/owner" },
            { label: "Projects", to: "/owner/projects" },
            { label: p.design?.name || p.id },
          ]}
        />
        {siblings.length > 1 && sibIndex >= 0 && (
          <ProjectPager
            index={sibIndex}
            total={siblings.length}
            prev={sibPrev}
            next={sibNext}
          />
        )}
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-3 flex-wrap">
            <WaitingOnPill
              stage={p.current_stage}
              testId="owner-pd-blocker"
            />
            {ownerBlocked && daysInStage != null && daysInStage < 60 && (
              <span
                className="wn-label tabular-nums"
                style={{
                  color: daysInStage >= 14 ? "#B14E2F" : "#6C6B85",
                  letterSpacing: "0.06em",
                }}
                data-testid="owner-pd-since"
              >
                · since {enteredLabel} · {daysInStage} day{daysInStage === 1 ? "" : "s"}
              </span>
            )}
            {ownerBlocked && (daysInStage == null || daysInStage >= 60) && enteredLabel && (
              <span
                className="wn-label"
                style={{ color: "#6C6B85" }}
                data-testid="owner-pd-since"
              >
                · since {enteredLabel}
              </span>
            )}
          </div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(28px, 4vw, 36px)",
              fontWeight: 600,
              color: "#1B1F4B",
              marginTop: 10,
            }}
            data-testid="owner-pd-title"
          >
            {p.design?.name || "Untitled project"}
          </h1>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14.5,
              marginTop: 4,
              lineHeight: 1.5,
            }}
            data-testid="owner-pd-context"
          >
            {p.parcel?.address || "—"} ·{" "}
            <Link
              to="/owner/messages"
              data-testid="owner-pd-architect-link"
              style={{
                color: "#272636",
                textDecoration: "none",
                borderBottom: "1px solid #D9D8E4",
                paddingBottom: 1,
              }}
            >
              {p.architect?.firm || "—"}
            </Link>
            {p.architect?.firm && (
              <>
                {" · "}
                <Link
                  to="/owner/messages"
                  className="inline-flex items-center"
                  style={{
                    gap: 4,
                    color: "#8F3C23",
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontStyle: "normal",
                    fontWeight: 700,
                    fontSize: 12.5,
                    letterSpacing: "0.02em",
                    textDecoration: "none",
                  }}
                  data-testid="owner-pd-message-architect"
                >
                  <MessageSquare size={12} strokeWidth={1.8} />
                  Message
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stage timeline (annotated with dates) */}
      <WorkflowStrip
        domain="project"
        current={p.current_stage}
        dateByStage={(p.stage_history || []).reduce((acc, h) => {
          if (h.stage && h.at) acc[h.stage] = h.at;
          return acc;
        }, {})}
        testId="owner-pd-timeline"
      />

      <div className="mt-7 grid lg:grid-cols-[2fr_1fr] gap-7">
        <div className="space-y-7">
          {/* Purchase plans */}
          {p.current_stage === "Purchase" && (
            <PurchaseCard
              p={p}
              planAmount={planAmount}
              open={purchaseOpen}
              setOpen={setPurchaseOpen}
              card={card}
              setCard={setCard}
              signature={signature}
              setSignature={setSignature}
              busy={busy === "purchase"}
              error={purchaseErr}
              onSubmit={submitPurchase}
            />
          )}

          {p.plan_purchase_receipt && (
            <ReceiptCard
              receipt={p.plan_purchase_receipt}
              receiptDoc={
                (p.documents || []).find(
                  (d) => d.doc_type === "payment_receipt"
                    && d.transaction_ref === p.plan_purchase_receipt.transaction_ref,
                )
              }
              onView={setViewDoc}
            />
          )}

          {/* Recommended contractor shortlist — surfaces the firms
              that have already built this exact plan, the moment the
              owner has paid for plans (project has moved into
              Expedited Review or beyond). Hidden when there are no
              prior builds (Approved-without-track-record designs
              like Stone Front Bungalow). */}
          {stageIdx >= STAGE_ORDER.indexOf("Expedited Review") && (
            <RecommendedContractorsCard p={p} />
          )}

          {/* Accela / Expedited Review */}
          {p.accela_ref && (
            <AccelaCard accela={p.accela_ref} />
          )}

          {/* Activation panel — visible from Build onwards (or whenever stage > Expedited Review) */}
          {stageIdx >= STAGE_ORDER.indexOf("Build") && (
            <ActivationPanel
              p={p}
              busy={busy}
              onTrigger={fireActivation}
            />
          )}

          {/* Construction inspection summary */}
          {(p.current_stage === "Construction Inspection" ||
            p.current_stage === "Complete") && (
            <InspectionsCard p={p} />
          )}

          {/* Architect ↔ Owner project chat — round-trips with the
              architect portal's MessagesCard. Always present. */}
          <ProjectChatCard p={p} userId={user?.id} onUpdated={setP} />

          {/* Documents */}
          <DocumentsCard
            p={p}
            busy={busy}
            onGenerate={generate}
            viewDoc={viewDoc}
            setViewDoc={setViewDoc}
            finalAvailable={
              p.current_stage === "Construction Inspection" ||
              p.current_stage === "Complete"
            }
          />

          {/* History — Timeline (narrative) + Audit trail (granular) */}
          <HistoryCard p={p} />
        </div>

        <aside className="space-y-5" data-testid="owner-pd-side">
          <DesignSummary p={p} />
          <ParcelSummary p={p} />
          <CostsCard p={p} />
        </aside>
      </div>
    </section>
  );
}

function ProjectChatCard({ p, userId, onUpdated }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const allMessages = p?.messages || [];
  const messages = showAll ? allMessages : allMessages.slice(-8);
  const hiddenCount = Math.max(0, allMessages.length - messages.length);
  const archName =
    p?.architect?.name || p?.architect?.firm || "your architect";
  async function send() {
    if (!text.trim() || !userId) return;
    setBusy(true);
    try {
      const updated = await sendOwnerProjectMessage(p.id, userId, text.trim());
      // POST response doesn't hydrate architect/parcel; merge from previous
      // state so the card doesn't lose context.
      onUpdated({
        ...updated,
        architect: p.architect,
        parcel: p.parcel,
        design: p.design,
      });
      setText("");
    } catch (err) {
      // Surface to console so a failed send is debuggable; UI stays
      // graceful (the input keeps its content because we don't clear
      // it on the error path — only on the success path above).
      console.error("Owner project chat send failed:", err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      className="wn-card"
      style={{ padding: "18px 22px" }}
      data-testid="owner-pd-project-chat"
    >
      <header className="flex items-center justify-between" style={{ gap: 12 }}>
        <div className="flex items-center" style={{ gap: 8 }}>
          <MessageSquare size={14} style={{ color: "#8F3C23" }} />
          <h2
            className="wn-label"
            style={{ color: "#8F3C23", letterSpacing: "0.06em" }}
          >
            Messages with {archName}
          </h2>
        </div>
        {allMessages.length > 8 && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 11.5,
              color: "#5B6196",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            data-testid="owner-pd-chat-show-all"
          >
            {showAll
              ? "Show less ↓"
              : `Show all (${allMessages.length}) ↑`}
          </button>
        )}
      </header>

      {allMessages.length === 0 ? (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 13.5,
            lineHeight: 1.55,
            marginTop: 10,
            maxWidth: 520,
          }}
          data-testid="owner-pd-chat-empty"
        >
          No messages yet. Send {archName} a note about your project — they’ll
          be notified and can reply from their workspace.
        </p>
      ) : (
        <div
          style={{
            marginTop: 12,
            maxHeight: showAll ? "none" : 320,
            overflowY: showAll ? "visible" : "auto",
            paddingRight: 4,
          }}
          data-testid="owner-pd-chat-list"
        >
          {messages.map((m, i) => (
            <div
              key={m.id || i}
              style={{
                padding: "10px 0",
                borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
              }}
              data-testid={`owner-pd-chat-message-${m.id || i}`}
            >
              <div className="flex items-baseline" style={{ gap: 8 }}>
                <span
                  style={{
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontWeight: 700,
                    fontSize: 12.5,
                    color: m.from_role === "owner" ? "#1B1F4B" : "#8F3C23",
                  }}
                >
                  {m.from_name ||
                    (m.from_role === "owner" ? "You" : archName)}
                </span>
                {m.at && (
                  <span
                    className="tabular-nums"
                    style={{
                      color: "#9AA0C0",
                      fontSize: 11.5,
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                    }}
                  >
                    {new Date(m.at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                )}
              </div>
              <p
                style={{
                  color: "#272636",
                  fontSize: 13.5,
                  marginTop: 3,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {m.body || m.text || ""}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-end" style={{ gap: 8 }}>
        <textarea
          className="wn-input"
          rows={2}
          style={{ flex: 1, padding: 10, fontSize: 13, lineHeight: 1.4 }}
          placeholder={`Message ${archName}…`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
          data-testid="owner-pd-chat-composer"
        />
        <button
          type="button"
          className="wn-btn wn-btn-primary"
          onClick={send}
          disabled={busy || !text.trim()}
          data-testid="owner-pd-chat-send"
          style={{ paddingLeft: 12, paddingRight: 12, fontSize: 12.5 }}
        >
          <Send size={12} />
          {busy ? "…" : "Send"}
        </button>
      </div>
    </section>
  );
}

function ProjectPager({ index, total, prev, next }) {
  return (
    <div
      className="flex items-center"
      style={{ gap: 12 }}
      data-testid="owner-pd-pager"
    >
      <span
        className="tabular-nums wn-label"
        style={{ color: "#6C6B85", letterSpacing: "0.04em" }}
      >
        Project {index + 1} of {total}
      </span>
      <div className="flex items-center" style={{ gap: 4 }}>
        {prev ? (
          <Link
            to={`/owner/projects/${prev.id}`}
            title={`Previous: ${prev.design?.name || prev.id}`}
            data-testid="owner-pd-pager-prev"
            className="inline-flex items-center justify-center"
            style={{
              width: 28, height: 28, borderRadius: 999,
              border: "1px solid #D9D8E4",
              color: "#1B1F4B",
              transition: "border-color 160ms ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#B14E2F")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#D9D8E4")}
          >
            <ChevronLeft size={14} />
          </Link>
        ) : (
          <span
            aria-hidden
            style={{
              width: 28, height: 28, borderRadius: 999,
              border: "1px solid #ECEBF1", color: "#9AA0C0",
              display: "inline-flex", alignItems: "center",
              justifyContent: "center", opacity: 0.5,
            }}
          >
            <ChevronLeft size={14} />
          </span>
        )}
        {next ? (
          <Link
            to={`/owner/projects/${next.id}`}
            title={`Next: ${next.design?.name || next.id}`}
            data-testid="owner-pd-pager-next"
            className="inline-flex items-center justify-center"
            style={{
              width: 28, height: 28, borderRadius: 999,
              border: "1px solid #D9D8E4",
              color: "#1B1F4B",
              transition: "border-color 160ms ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#B14E2F")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#D9D8E4")}
          >
            <ChevronRight size={14} />
          </Link>
        ) : (
          <span
            aria-hidden
            style={{
              width: 28, height: 28, borderRadius: 999,
              border: "1px solid #ECEBF1", color: "#9AA0C0",
              display: "inline-flex", alignItems: "center",
              justifyContent: "center", opacity: 0.5,
            }}
          >
            <ChevronRight size={14} />
          </span>
        )}
      </div>
    </div>
  );
}

/* Dated stage timeline — annotates each step with its stage_history date
   when available. Falls back gracefully when the seed lacks an entry. */
function DatedTimeline({ p, stageIdx }) {
  const history = p.stage_history || [];
  // Map stage → date for quick lookup.
  const dateByStage = history.reduce((acc, h) => {
    if (h.stage && h.at) acc[h.stage] = h.at;
    return acc;
  }, {});
  return (
    <div
      className="mt-6"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        padding: "20px 16px 16px",
      }}
      data-testid="owner-pd-timeline"
    >
      <div className="grid grid-cols-7 gap-2 items-start">
        {STAGE_ORDER.map((s, i) => {
          const done = i < stageIdx;
          const current = i === stageIdx;
          const at = dateByStage[s];
          const date = at
            ? new Date(at).toLocaleDateString("en-US", {
                month: "short", day: "numeric",
              })
            : null;
          return (
            <div
              key={s}
              className="flex flex-col items-center text-center"
              style={{ minWidth: 0 }}
            >
              <span
                className="tabular-nums"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  background: current ? "#1B1F4B" : done ? "#5B6196" : "#FBFAFD",
                  border:
                    "1px solid " +
                    (current || done ? "transparent" : "#D9D8E4"),
                  color: current || done ? "#FBFAFD" : "#6C6B85",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {done ? <Check size={12} /> : i + 1}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: current ? "#1B1F4B" : done ? "#272636" : "#6C6B85",
                  marginTop: 6,
                  fontWeight: current ? 700 : 500,
                  fontFamily: '"Nunito Sans", sans-serif',
                  letterSpacing: "0.01em",
                  lineHeight: 1.25,
                }}
              >
                {s}
              </span>
              {date && (done || current) && (
                <span
                  className="tabular-nums"
                  style={{
                    marginTop: 2,
                    fontSize: 10.5,
                    color: "#6C6B85",
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    lineHeight: 1.2,
                  }}
                >
                  {current ? `entered ${date}` : date}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WhatHappensNext() {
  // Calendar days (14) ≈ "10 business days" for the City Expedited Review SLA.
  const decisionDate = new Date(Date.now() + 14 * 86400000)
    .toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return (
    <div
      className="mt-6"
      style={{
        borderTop: "1px solid #ECEBF1",
        paddingTop: 16,
      }}
      data-testid="owner-pd-whats-next"
    >
      <div className="wn-label" style={{ color: "#6C6B85" }}>
        What happens next
      </div>
      <ul
        className="mt-3 grid grid-cols-1 md:grid-cols-3"
        style={{
          gap: 14, listStyle: "none", padding: 0, margin: 0,
        }}
      >
        <li className="flex items-start" style={{ gap: 10 }}>
          <Check size={14} color="#4F6149" strokeWidth={2} style={{ marginTop: 3, flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: "#272636", lineHeight: 1.45 }}>
            Plans delivered to your project. Receipt emailed instantly.
          </span>
        </li>
        <li className="flex items-start" style={{ gap: 10 }}>
          <Calendar size={14} color="#3A5563" strokeWidth={1.8} style={{ marginTop: 3, flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: "#272636", lineHeight: 1.45 }}>
            10-day Expedited Review begins with the City of Indianapolis.
          </span>
        </li>
        <li className="flex items-start" style={{ gap: 10 }}>
          <ArrowRight size={14} color="#8F3C23" strokeWidth={1.8} style={{ marginTop: 3, flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: "#272636", lineHeight: 1.45 }}>
            Expected permit decision by{" "}
            <span style={{ color: "#1B1F4B", fontWeight: 700 }}>{decisionDate}</span>.
          </span>
        </li>
      </ul>
    </div>
  );
}

function PurchaseCard({
  p, planAmount, open, setOpen, card, setCard,
  signature, setSignature, busy, error, onSubmit,
}) {
  // Multi-step modal: 1) Summary & terms → 2) Sign + Pay → 3) Awaiting countersign / Plans delivered.
  const [step, setStep] = useState(1);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const designName = p.design?.name || "this design";
  const sqft = p.design?.sqft || 0;
  const ready =
    card.card_number.trim().length >= 12 &&
    card.expiry.trim().length >= 4 &&
    card.cvc.trim().length >= 3 &&
    (signature?.value || "").trim().length > 0 &&
    (signature?.method !== "drawn" || (signature?.typed_name || "").trim().length > 0);

  const contract = p.purchase_contract || null;
  const contractStatus = contract?.status || null;

  // When the contract is in-flight (owner signed, not yet executed),
  // jump straight to step 3 of the modal whenever it's opened.
  useEffect(() => {
    if (!open) return;
    if (contractStatus === "owner_signed" || contractStatus === "executed") {
      setStep(3);
    }
  }, [open, contractStatus]);

  // Reset to step 1 when reopening on a fresh slate (no contract yet or
  // a rejected one — owner can re-attempt).
  useEffect(() => {
    if (!open) {
      setStep(1);
      setAcceptedTerms(false);
    }
  }, [open]);

  // If the contract is locked, the entry card becomes a status block
  // instead of the "Begin checkout" CTA.
  if (contractStatus === "owner_signed") {
    return (
      <ContractAwaitingCard
        p={p}
        contract={contract}
        planAmount={planAmount}
      />
    );
  }

  return (
    <>
      {/* Inline editorial card — the entry point */}
      <div
        style={{
          border: "1px solid #D9D8E4",
          borderRadius: 4,
          backgroundColor: "#FBFAFD",
          padding: 24,
        }}
        data-testid="owner-pd-purchase"
      >
        <div className="flex items-start justify-between gap-6">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 22,
                  height: 2,
                  background: "#B14E2F",
                }}
              />
              <span className="wn-label" style={{ color: "#8F3C23" }}>
                Stage gate · Purchase plans
              </span>
            </div>
            <h2
              className="mt-3"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 500,
                fontSize: 26,
                color: "#1B1F4B",
                lineHeight: 1.18,
                margin: 0,
              }}
            >
              Buy the construction plans for {designName}.
            </h2>
            <p
              className="mt-3"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#272636",
                fontSize: 16,
                lineHeight: 1.55,
                margin: 0,
                maxWidth: 640,
              }}
            >
              Plans include stamped architectural drawings, structural notes,
              and the plan-set you'll submit to DBNS. Purchasing also unlocks
              your project's <span style={{ color: "#1B1F4B" }}>10-day Expedited Review</span> with the City of Indianapolis.
            </p>

            <ul
              className="mt-5 grid grid-cols-1 md:grid-cols-2"
              style={{ gap: 10, listStyle: "none", padding: 0, margin: 0 }}
            >
              {[
                "Step 1 — Review the plan-set summary and platform terms",
                "Step 2 — Enter payment details · charge processed securely",
                "Step 3 — Plans delivered to your project · receipt emailed",
                "Step 4 — Expedited Review queued automatically with the City",
              ].map((s, i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-2"
                  style={{
                    color: "#272636",
                    fontSize: 13.5,
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    className="tabular-nums"
                    style={{
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontWeight: 700,
                      color: "#8F3C23",
                      letterSpacing: "0.04em",
                      flexShrink: 0,
                    }}
                  >
                    0{i + 1}
                  </span>
                  {s.replace(/^Step \d+ — /, "")}
                </li>
              ))}
            </ul>
          </div>

          <aside
            style={{
              minWidth: 200,
              borderLeft: "1px solid #D9D8E4",
              paddingLeft: 20,
            }}
          >
            <div className="wn-label" style={{ color: "#6C6B85" }}>
              Total today
            </div>
            <div
              className="tabular-nums mt-1"
              style={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 800,
                fontSize: 30,
                color: "#1B1F4B",
                lineHeight: 1,
                letterSpacing: "-0.01em",
              }}
            >
              ${planAmount.toLocaleString()}
            </div>
            <div
              className="mt-1"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              $2,500 base + ${formatInt(sqft)} sqft
            </div>
          </aside>
        </div>

        <button
          className="wn-btn wn-btn-primary mt-6 inline-flex items-center"
          style={{ gap: 8 }}
          onClick={() => setOpen(true)}
          data-testid="owner-pd-purchase-open"
        >
          Begin checkout · ${planAmount.toLocaleString()}
        </button>

        {/* What happens next */}
        <WhatHappensNext />
      </div>

      {/* Modal */}
      {open && (
        <PurchaseModal
          onClose={() => setOpen(false)}
          step={step}
          setStep={setStep}
          p={p}
          planAmount={planAmount}
          card={card}
          setCard={setCard}
          signature={signature}
          setSignature={setSignature}
          busy={busy}
          error={error}
          onSubmit={onSubmit}
          ready={ready}
          acceptedTerms={acceptedTerms}
          setAcceptedTerms={setAcceptedTerms}
        />
      )}
    </>
  );
}

function ContractPreview({ p, planAmount }) {
  const sqft = p.design?.sqft || 0;
  const designName = p.design?.name || "Construction plans";
  const ownerName = p.owner?.name || p.owner?.organization || "Owner of record";
  const ownerOrg = p.owner?.organization || "";
  const ownerEmail = p.owner?.email || "";
  const archFirm = p.architect?.firm || "Architect of record";
  const archName = p.architect?.principal_name || p.architect?.name || "";
  const archEmail = p.architect?.email || "";
  const parcelAddr = p.parcel?.address || "—";
  return (
    <div
      data-testid="owner-pd-contract-preview"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 6,
        background: "#FBFAFD",
        marginBottom: 18,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "14px 18px",
          borderBottom: "1px solid #ECEBF1",
          background: "#F5F4FA",
        }}
      >
        <div className="wn-label" style={{ color: "#8F3C23" }}>
          Construction plans purchase agreement
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            fontSize: 12,
            color: "#6C6B85",
            marginTop: 2,
          }}
        >
          Template v1-2026 · Governing law: State of Indiana, Marion County
        </div>
      </div>
      <div
        style={{
          maxHeight: 260,
          overflow: "auto",
          padding: "16px 18px",
          fontFamily: '"Newsreader", Georgia, serif',
          fontSize: 12.5,
          color: "#272636",
          lineHeight: 1.55,
        }}
      >
        <p style={{ margin: 0 }}>
          <span style={{ color: "#A23B2E", fontWeight: 700 }}>
            TEMPLATE — PLACEHOLDER LEGAL LANGUAGE, NOT FOR PRODUCTION USE.
          </span>{" "}
          This agreement is entered into between the Owner and the
          Architect named below for the purchase, licensing, and
          delivery of construction plans for a single residential build
          at the parcel identified herein.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <div className="wn-label" style={{ color: "#6C6B85" }}>Owner / Client</div>
            <div style={{ color: "#1B1F4B", fontWeight: 600, marginTop: 3 }}>
              {ownerName}
            </div>
            {ownerOrg && (
              <div style={{ fontSize: 12 }}>{ownerOrg}</div>
            )}
            <div style={{ fontSize: 12, color: "#6C6B85" }}>{ownerEmail || "—"}</div>
          </div>
          <div>
            <div className="wn-label" style={{ color: "#6C6B85" }}>Architect of record</div>
            <div style={{ color: "#1B1F4B", fontWeight: 600, marginTop: 3 }}>
              {archFirm}
            </div>
            {archName && (
              <div style={{ fontSize: 12 }}>{archName}</div>
            )}
            <div style={{ fontSize: 12, color: "#6C6B85" }}>{archEmail || "—"}</div>
          </div>
          <div>
            <div className="wn-label" style={{ color: "#6C6B85" }}>Subject of agreement</div>
            <div style={{ color: "#1B1F4B", fontWeight: 600, marginTop: 3 }}>
              {designName}
            </div>
            <div style={{ fontSize: 12, color: "#6C6B85" }}>
              {formatInt(sqft)} sqft · {parcelAddr}
            </div>
          </div>
          <div>
            <div className="wn-label" style={{ color: "#6C6B85" }}>Total payable</div>
            <div
              className="tabular-nums"
              style={{
                color: "#1B1F4B",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 800,
                fontSize: 18,
                marginTop: 3,
                letterSpacing: "-0.01em",
              }}
            >
              ${planAmount.toLocaleString()}
            </div>
            <div style={{ fontSize: 11.5, color: "#6C6B85" }}>
              $2,500 base + ${formatInt(sqft)} sqft fee
            </div>
          </div>
        </div>
        <ol className="mt-4" style={{ paddingLeft: 18, margin: "10px 0 0" }}>
          <li>
            Architect agrees to deliver stamped construction drawings,
            structural notes, and the DBNS plan-set.
          </li>
          <li>
            Payment is held in escrow until the Architect countersigns;
            funds are then released and the 10-day Expedited Review is
            queued automatically with the City of Indianapolis.
          </li>
          <li>
            If the Architect rejects, the Owner is refunded in full.
            No obligations accrue to either party.
          </li>
          <li>
            Plans are licensed for a single build at the parcel of
            record. Refunds within 30 days minus a $250 administrative
            fee.
          </li>
        </ol>
      </div>
    </div>
  );
}

function ContractAwaitingCard({ p, contract, planAmount }) {
  const firm = p.architect?.firm || "your architect";
  const sentAt = contract?.owner_signature?.signed_at
    ? new Date(contract.owner_signature.signed_at).toLocaleDateString("en-US", {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
      })
    : "—";
  const contractPdfUrl = (() => {
    const doc = (p.documents || []).find(
      (d) => d.doc_type === "purchase_contract"
        && d.contract_id === contract?.contract_id,
    );
    return doc?.url || null;
  })();
  return (
    <div
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        padding: 24,
      }}
      data-testid="owner-pd-contract-awaiting"
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 22,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          Stage gate · Awaiting architect countersign
        </span>
      </div>
      <h2
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 24,
          color: "#1B1F4B",
          lineHeight: 1.2,
          margin: 0,
        }}
      >
        Contract sent to {firm} for countersignature.
      </h2>
      <p
        className="mt-3"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#272636",
          fontSize: 15.5,
          lineHeight: 1.55,
          margin: 0,
          maxWidth: 640,
        }}
      >
        You signed and paid <span style={{ color: "#1B1F4B" }}>${planAmount.toLocaleString()}</span> on{" "}
        <span style={{ color: "#1B1F4B" }}>{sentAt}</span>. Funds are held
        in escrow and will be released the moment the architect countersigns.
        If they decline, your payment is refunded in full automatically.
      </p>
      <div
        className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4"
        style={{ paddingTop: 16, borderTop: "1px solid #ECEBF1" }}
      >
        <ContractKv label="Contract reference" value={contract?.contract_id || "—"} mono />
        <ContractKv label="Amount held" value={`$${planAmount.toLocaleString()}`} />
        <ContractKv label="Status" value="Awaiting countersign" emphasis="terracotta" />
      </div>
      {contractPdfUrl && (
        <div className="mt-5">
          <a
            href={contractPdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="wn-btn wn-btn-tertiary inline-flex items-center"
            style={{ gap: 8 }}
            data-testid="owner-pd-contract-view"
          >
            <Download size={14} /> Download the signed contract (PDF)
          </a>
        </div>
      )}
    </div>
  );
}

function ContractKv({ label, value, mono, emphasis }) {
  const color = emphasis === "terracotta" ? "#8F3C23" : "#1B1F4B";
  return (
    <div>
      <div className="wn-label" style={{ color: "#6C6B85" }}>{label}</div>
      <div
        className={mono ? "tabular-nums" : ""}
        style={{
          marginTop: 4,
          fontFamily: mono
            ? '"Nunito Sans", sans-serif'
            : '"Fraunces", Georgia, serif',
          fontWeight: mono ? 700 : 500,
          color,
          fontSize: 16,
          letterSpacing: mono ? "0.04em" : "normal",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function PurchaseModal({
  onClose, step, setStep, p, planAmount, card, setCard,
  signature, setSignature,
  busy, error, onSubmit, ready, acceptedTerms, setAcceptedTerms,
}) {
  const sqft = p.design?.sqft || 0;
  const designName = p.design?.name || "this design";
  const lineItems = [
    { label: "Plan-set base fee", amount: 2500 },
    { label: `Per-square-foot (${formatInt(sqft)} sqft × $1)`, amount: sqft || 0 },
  ];
  // Lock body scroll while the modal is open.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, []);
  // Portal to document.body so an ancestor `transform` (e.g. the page's
  // `animate-fade-in-up` translateY) doesn't become the containing block
  // for our `position: fixed` overlay — which would push it off-centre.
  return ReactDOM.createPortal((
    <div
      role="dialog"
      aria-modal="true"
      data-testid="purchase-modal"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(27,31,75,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(760px, 100%)",
          maxHeight: "92vh",
          overflow: "auto",
          background: "#FBFAFD",
          border: "1px solid #D9D8E4",
          borderRadius: 6,
          boxShadow: "0 28px 60px rgba(27,31,75,0.30)",
        }}
      >
        {/* Header / stepper */}
        <div
          style={{
            padding: "22px 28px 18px",
            borderBottom: "1px solid #D9D8E4",
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div style={{ minWidth: 0 }}>
              <div className="wn-label" style={{ color: "#8F3C23" }}>
                Checkout · {designName}
              </div>
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 500,
                  fontSize: 24,
                  color: "#1B1F4B",
                  marginTop: 4,
                  lineHeight: 1.15,
                  letterSpacing: "-0.005em",
                }}
              >
                {step === 1
                  ? "Confirm what you're buying."
                  : step === 2
                  ? "Sign the contract and pay."
                  : (p.purchase_contract?.status === "executed"
                      || p.current_stage !== "Purchase")
                  ? "Plans delivered."
                  : "Sent for countersign."}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="purchase-modal-close"
              aria-label="Close"
              style={{
                width: 32,
                height: 32,
                borderRadius: 4,
                border: "1px solid #D9D8E4",
                background: "transparent",
                color: "#6C6B85",
                cursor: "pointer",
                fontSize: 18,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
          <div
            className="flex items-center"
            style={{ gap: 6, marginTop: 18 }}
          >
            {[1, 2, 3].map((n) => (
              <span
                key={n}
                data-testid={`purchase-step-${n}`}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 999,
                  background:
                    n <= step ? "#B14E2F" : "#ECEBF1",
                  transition: "background-color 200ms ease",
                }}
              />
            ))}
          </div>
        </div>

        {/* Step content */}
        <div style={{ padding: "20px 28px 22px" }}>
          {step === 1 && (
            <>
              <div className="wn-label" style={{ color: "#6C6B85" }}>
                Itemised total
              </div>
              <table
                className="mt-2"
                style={{ width: "100%", borderCollapse: "collapse" }}
              >
                <tbody>
                  {lineItems.map((li, i) => (
                    <tr
                      key={i}
                      style={{ borderTop: "1px solid #ECEBF1" }}
                    >
                      <td
                        style={{
                          padding: "12px 0",
                          fontSize: 14,
                          color: "#272636",
                        }}
                      >
                        {li.label}
                      </td>
                      <td
                        className="tabular-nums"
                        style={{
                          padding: "12px 0",
                          textAlign: "right",
                          color: "#1B1F4B",
                          fontWeight: 600,
                        }}
                      >
                        ${li.amount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid #D9D8E4" }}>
                    <td
                      style={{
                        padding: "12px 0",
                        fontFamily: '"Fraunces", Georgia, serif',
                        fontSize: 18,
                        color: "#1B1F4B",
                        fontWeight: 600,
                      }}
                    >
                      Total today
                    </td>
                    <td
                      className="tabular-nums"
                      style={{
                        padding: "12px 0",
                        textAlign: "right",
                        color: "#1B1F4B",
                        fontWeight: 800,
                        fontSize: 18,
                      }}
                    >
                      ${planAmount.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                className="mt-5"
                style={{
                  border: "1px solid #D9D8E4",
                  borderRadius: 4,
                  padding: "16px 18px",
                  background: "#F5F4FA",
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: "#272636",
                }}
              >
                <span className="wn-label" style={{ color: "#1B1F4B" }}>
                  What you're agreeing to
                </span>
                <ul
                  className="mt-2"
                  style={{ paddingLeft: 18, margin: 0, color: "#272636" }}
                >
                  <li>
                    Plans are licensed for a single build at the parcel of
                    record.
                  </li>
                  <li>
                    Refunds within 30 days, minus a $250 administrative fee.
                  </li>
                  <li>
                    Modifications must be commissioned through the architect
                    of record.
                  </li>
                </ul>
              </div>

              <label
                className="flex items-start mt-4"
                style={{ gap: 10, cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  data-testid="purchase-accept-terms"
                  style={{
                    width: 16,
                    height: 16,
                    marginTop: 3,
                    accentColor: "#1B1F4B",
                  }}
                />
                <span style={{ fontSize: 13.5, color: "#272636" }}>
                  I have reviewed the line items and accept the licence and
                  refund terms.
                </span>
              </label>
            </>
          )}

          {step === 2 && (
            <>
              {/* Mini summary card — gives the buyer context anchoring */}
              <div
                style={{
                  border: "1px solid #D9D8E4",
                  borderRadius: 4,
                  padding: "14px 16px",
                  background: "#F5F4FA",
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  alignItems: "center",
                  marginBottom: 22,
                }}
              >
                <div>
                  <div className="wn-label" style={{ color: "#6C6B85" }}>
                    Paying for
                  </div>
                  <div
                    style={{
                      fontFamily: '"Fraunces", Georgia, serif',
                      fontWeight: 500,
                      color: "#1B1F4B",
                      fontSize: 17,
                      lineHeight: 1.2,
                      marginTop: 2,
                    }}
                  >
                    {designName} — Construction plans
                  </div>
                  <div
                    style={{
                      color: "#6C6B85",
                      fontSize: 12.5,
                      marginTop: 2,
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                    }}
                  >
                    Stamped drawings + 10-day Expedited Review
                  </div>
                </div>
                <div
                  className="tabular-nums"
                  style={{
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontWeight: 800,
                    color: "#1B1F4B",
                    fontSize: 22,
                    lineHeight: 1,
                  }}
                >
                  ${planAmount.toLocaleString()}
                </div>
              </div>

              <ContractPreview p={p} planAmount={planAmount} />

              <div
                className="mt-5"
                style={{
                  borderTop: "1px solid #ECEBF1",
                  paddingTop: 18,
                }}
              >
                <div className="wn-label" style={{ color: "#1B1F4B" }}>
                  Sign the contract
                </div>
                <p
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    fontSize: 13,
                    color: "#6C6B85",
                    margin: "4px 0 14px",
                    lineHeight: 1.5,
                  }}
                >
                  Type to sign or draw your signature. Your name and signature
                  will be embedded in the contract PDF and timestamped.
                </p>
                <ContractSignaturePad
                  value={signature}
                  onChange={setSignature}
                  testidPrefix="owner-pd-contract-sig"
                  placeholderName="Type your full legal name"
                />
              </div>

              <div
                className="mt-5"
                style={{
                  borderTop: "1px solid #ECEBF1",
                  paddingTop: 18,
                }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Lock size={14} style={{ color: "#5B6196" }} />
                  <span className="wn-label" style={{ color: "#5B6196" }}>
                    Simulated payment — no real card charged. Use 4242 4242 4242 4242.
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
                  <ModalField label="Card number">
                    <input
                      className="tabular-nums"
                      placeholder="4242 4242 4242 4242"
                      value={card.card_number}
                      onChange={(e) => setCard({ ...card, card_number: e.target.value })}
                      data-testid="owner-pd-card"
                      style={modalInputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#B14E2F")}
                      onBlur={(e) => (e.target.style.borderColor = "#D9D8E4")}
                    />
                  </ModalField>
                  <ModalField label="Expiry">
                    <input
                      className="tabular-nums"
                      placeholder="MM/YY"
                      value={card.expiry}
                      onChange={(e) => setCard({ ...card, expiry: e.target.value })}
                      data-testid="owner-pd-expiry"
                      style={modalInputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#B14E2F")}
                      onBlur={(e) => (e.target.style.borderColor = "#D9D8E4")}
                    />
                  </ModalField>
                  <ModalField label="CVC">
                    <input
                      className="tabular-nums"
                      placeholder="123"
                      value={card.cvc}
                      onChange={(e) => setCard({ ...card, cvc: e.target.value })}
                      data-testid="owner-pd-cvc"
                      style={modalInputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#B14E2F")}
                      onBlur={(e) => (e.target.style.borderColor = "#D9D8E4")}
                    />
                  </ModalField>
                </div>

                <p
                  className="mt-3"
                  style={{
                    color: "#6C6B85",
                    fontSize: 12,
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    margin: "10px 0 0",
                  }}
                >
                  We never store full card numbers. Funds are held in escrow
                  until the architect countersigns — refunded automatically
                  on rejection.
                </p>
              </div>

              {error && (
                <div
                  className="mt-3"
                  style={{ color: "#A23B2E", fontSize: 13 }}
                  data-testid="owner-pd-purchase-error"
                >
                  {error}
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <>
              {(() => {
                const isExecuted =
                  (p.purchase_contract?.status === "executed")
                  || (p.current_stage !== "Purchase"
                      && !!p.plan_purchase_receipt);
                if (!isExecuted) {
                  return (
                    <>
                      <div
                        className="flex items-center"
                        style={{ gap: 12, marginBottom: 14 }}
                      >
                        <span
                          aria-hidden
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 999,
                            background: "rgba(143,60,35,0.10)",
                            color: "#8F3C23",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Send size={20} strokeWidth={2.2} />
                        </span>
                        <div>
                          <div
                            style={{
                              fontFamily: '"Fraunces", Georgia, serif',
                              fontWeight: 500,
                              color: "#1B1F4B",
                              fontSize: 22,
                              lineHeight: 1.2,
                            }}
                          >
                            Contract sent — awaiting countersign.
                          </div>
                          <div
                            style={{
                              fontFamily: '"Newsreader", Georgia, serif',
                              fontStyle: "italic",
                              color: "#6C6B85",
                              fontSize: 14,
                              marginTop: 2,
                            }}
                          >
                            Funds held in escrow · {p.architect?.firm || "your architect"} notified by email.
                          </div>
                        </div>
                      </div>
                      <p
                        style={{
                          color: "#272636",
                          fontSize: 13.5,
                          lineHeight: 1.55,
                          margin: "8px 0 0",
                        }}
                      >
                        Your stamped plans and the City Expedited Review will
                        be queued the moment {p.architect?.firm || "your architect"}{" "}
                        countersigns the agreement. If they decline, your
                        payment is refunded automatically and you can
                        re-attempt with a different architect.
                      </p>
                      {p.purchase_contract?.contract_id && (
                        <div
                          className="mt-4"
                          data-testid="owner-pd-purchase-contract-ref"
                          style={{
                            border: "1px solid #D9D8E4",
                            borderRadius: 4,
                            padding: "14px 16px",
                            background: "#F5F4FA",
                          }}
                        >
                          <div className="wn-label" style={{ color: "#6C6B85" }}>
                            Contract reference
                          </div>
                          <div
                            className="tabular-nums"
                            style={{
                              fontFamily: '"Nunito Sans", sans-serif',
                              fontWeight: 700,
                              fontSize: 16,
                              color: "#1B1F4B",
                              letterSpacing: "0.04em",
                              marginTop: 4,
                            }}
                          >
                            {p.purchase_contract.contract_id}
                          </div>
                        </div>
                      )}
                    </>
                  );
                }
                return (
                  <>
                    <div
                      className="flex items-center"
                      style={{ gap: 12, marginBottom: 14 }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 999,
                          background: "rgba(79,97,73,0.14)",
                          color: "#4F6149",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Check size={22} strokeWidth={2.2} />
                      </span>
                      <div>
                        <div
                          style={{
                            fontFamily: '"Fraunces", Georgia, serif',
                            fontWeight: 500,
                            color: "#1B1F4B",
                            fontSize: 22,
                            lineHeight: 1.2,
                          }}
                        >
                          Plans delivered.
                        </div>
                        <div
                          style={{
                            fontFamily: '"Newsreader", Georgia, serif',
                            fontStyle: "italic",
                            color: "#6C6B85",
                            fontSize: 14,
                            marginTop: 2,
                          }}
                        >
                          Receipt emailed · Expedited Review queued.
                        </div>
                      </div>
                    </div>
                    {p.plan_purchase_receipt && (
                      <div
                        style={{
                          border: "1px solid #D9D8E4",
                          borderRadius: 4,
                          padding: "14px 16px",
                          background: "#F5F4FA",
                        }}
                      >
                        <div className="wn-label" style={{ color: "#6C6B85" }}>
                          Reference
                        </div>
                        <div
                          className="tabular-nums"
                          style={{
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontWeight: 700,
                            fontSize: 16,
                            color: "#1B1F4B",
                            letterSpacing: "0.04em",
                            marginTop: 4,
                          }}
                        >
                          {p.plan_purchase_receipt.transaction_ref}
                        </div>
                        <div
                          style={{
                            color: "#272636",
                            fontSize: 13,
                            marginTop: 6,
                          }}
                        >
                          ${(p.plan_purchase_receipt.amount || 0).toLocaleString()} {p.plan_purchase_receipt.currency} · card ending {p.plan_purchase_receipt.last4}
                        </div>
                      </div>
                    )}
                    <p
                      className="mt-4"
                      style={{
                        color: "#272636",
                        fontSize: 13.5,
                        lineHeight: 1.55,
                        margin: 0,
                      }}
                    >
                      Find this receipt anytime under <span style={{ color: "#1B1F4B", fontWeight: 600 }}>Payments</span> in the
                      navigation, and your stamped plans under <span style={{ color: "#1B1F4B", fontWeight: 600 }}>Documents</span>.
                    </p>
                  </>
                );
              })()}
            </>
          )}
        </div>

        {/* Footer / actions */}
        <div
          className="flex items-center justify-between"
          style={{
            padding: "14px 28px 18px",
            borderTop: "1px solid #D9D8E4",
            gap: 12,
          }}
        >
          {step === 1 && (
            <>
              <button
                type="button"
                className="wn-btn wn-btn-tertiary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="wn-btn wn-btn-primary"
                disabled={!acceptedTerms}
                onClick={() => setStep(2)}
                data-testid="purchase-next-payment"
                style={{
                  opacity: acceptedTerms ? 1 : 0.55,
                  cursor: acceptedTerms ? "pointer" : "not-allowed",
                }}
              >
                Continue to contract & payment →
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                style={modalBackBtnStyle}
              >
                ← Back
              </button>
              <button
                type="button"
                className="wn-btn wn-btn-primary"
                disabled={!ready || busy}
                onClick={onSubmit}
                data-testid="owner-pd-purchase-confirm"
                style={{
                  opacity: !ready || busy ? 0.55 : 1,
                  cursor: !ready || busy ? "not-allowed" : "pointer",
                }}
              >
                {busy ? "Processing…" : `Sign & pay $${planAmount.toLocaleString()}`}
              </button>
            </>
          )}
          {step === 3 && (
            <>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="wn-btn wn-btn-primary"
                onClick={onClose}
                data-testid="purchase-done"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}

function ReceiptCard({ receipt, receiptDoc, onView }) {
  return (
    <div className="wn-card" style={{ padding: 18, background: "#FBFAFD" }} data-testid="owner-pd-receipt">
      <div className="wn-label">Plan purchase receipt</div>
      <div
        className="tabular-nums"
        style={{ color: "#272636", fontSize: 13, marginTop: 4 }}
      >
        {receipt.transaction_ref} · ${receipt.amount.toLocaleString()} {receipt.currency} · card{" "}
        ending {receipt.last4}
      </div>
      <div style={{ color: "#6C6B85", fontSize: 11.5 }}>
        Paid {relativeDate(receipt.paid_at)}
      </div>
      {receiptDoc && (
        <button
          type="button"
          onClick={() => onView(receiptDoc)}
          data-testid="owner-pd-receipt-view"
          className="mt-3 inline-flex items-center"
          style={{
            gap: 6,
            color: "#8F3C23",
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            fontSize: 12.5,
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          View receipt <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// RecommendedContractorsCard — the moment the buyer pays for plans
// becomes the moment they get a hand-off shortlist of firms that have
// already built this exact plan. Sourced from design.prior_builds —
// the same data that drives the design-detail "Previously built"
// block and the marketplace "N BUILT" pill. Grouped by trade
// (General contractor / Electrical / Plumbing / Mechanical) and
// sorted within each group by number-of-builds-on-this-plan, so the
// firm at the top of each column is the firm most familiar with the
// plan's specifics.
// ───────────────────────────────────────────────────────────────────
function RecommendedContractorsCard({ p }) {
  const builds = p?.design?.prior_builds || [];
  if (builds.length === 0) return null;

  // Aggregate contractors across all builds, deduped by license_no,
  // tracking how many builds each one pulled permits on.
  const byKey = new Map();
  builds.forEach((b) => {
    const seenInBuild = new Set();
    (b.contractors || []).forEach((c) => {
      const key = (c.license_no || c.name || "").trim();
      if (!key || seenInBuild.has(key)) return;
      seenInBuild.add(key);
      const existing = byKey.get(key);
      if (existing) {
        existing.builds += 1;
        existing.totalWork += Number(c.work_cost || 0);
        existing.latestPermitDate =
          c.permit_date > existing.latestPermitDate
            ? c.permit_date
            : existing.latestPermitDate;
      } else {
        byKey.set(key, {
          key,
          name: c.name,
          role: c.role,
          license_no: c.license_no,
          builds: 1,
          totalWork: Number(c.work_cost || 0),
          latestPermitDate: c.permit_date || "",
        });
      }
    });
  });

  const all = Array.from(byKey.values());
  if (all.length === 0) return null;

  // Group by role, then sort each group by build count desc, then by
  // most-recent permit date.
  const groups = {};
  all.forEach((c) => {
    const role = c.role || "Other trade";
    if (!groups[role]) groups[role] = [];
    groups[role].push(c);
  });
  // Stable role order: GC first, then trades, then anything else.
  const ROLE_ORDER = [
    "General contractor",
    "Electrical",
    "Plumbing",
    "Mechanical",
  ];
  const orderedRoles = [
    ...ROLE_ORDER.filter((r) => groups[r]),
    ...Object.keys(groups).filter((r) => !ROLE_ORDER.includes(r)),
  ];
  Object.values(groups).forEach((arr) => {
    arr.sort((a, b) => {
      if (b.builds !== a.builds) return b.builds - a.builds;
      return (b.latestPermitDate || "").localeCompare(
        a.latestPermitDate || "",
      );
    });
  });

  const designName = p?.design?.name || "this plan";

  return (
    <div
      className="wn-card"
      style={{ padding: 18 }}
      data-testid="owner-pd-recommended-contractors"
    >
      <div className="flex items-start justify-between" style={{ gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            className="wn-label"
            style={{ color: "#4F6149", letterSpacing: "0.08em" }}
          >
            Recommended contractor shortlist
          </div>
          <h3
            className="mt-1"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: 22,
              color: "#1B1F4B",
              margin: 0,
              lineHeight: 1.2,
              letterSpacing: "-0.005em",
            }}
          >
            {all.length} licensed{" "}
            {all.length === 1 ? "firm has" : "firms have"} already built
            this plan.
          </h3>
          <p
            className="mt-2"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13.5,
              lineHeight: 1.55,
              margin: 0,
              maxWidth: 640,
            }}
          >
            Pulled from City of Indianapolis DBNS permit records across{" "}
            {builds.length}{" "}
            {builds.length === 1 ? "completed build" : "completed builds"}{" "}
            of {designName}. The firm at the top of each trade column is
            the one with the most prior builds of this specific plan.
          </p>
        </div>
      </div>

      <div
        className="mt-5"
        style={{
          display: "grid",
          gridTemplateColumns:
            orderedRoles.length <= 2
              ? "1fr"
              : "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 18,
        }}
      >
        {orderedRoles.map((role) => (
          <div
            key={role}
            data-testid={`owner-pd-recommended-group-${role
              .toLowerCase()
              .replace(/\s+/g, "-")}`}
            style={{
              border: "1px solid #DDD9CE",
              borderRadius: 4,
              padding: 14,
              background: "#FBFAFD",
            }}
          >
            <div
              className="wn-label"
              style={{ color: "#8F3C23", letterSpacing: "0.08em" }}
            >
              {role}
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: "8px 0 0",
              }}
            >
              {groups[role].map((c, i) => (
                <li
                  key={c.key}
                  data-testid={`owner-pd-recommended-firm-${c.key}`}
                  style={{
                    paddingTop: i === 0 ? 0 : 10,
                    paddingBottom: 10,
                    borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
                  }}
                >
                  <div
                    className="flex items-center"
                    style={{ gap: 8, flexWrap: "wrap" }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        color: "#1B1F4B",
                        fontSize: 14,
                      }}
                    >
                      {c.name}
                    </span>
                    {c.builds > 1 && (
                      <span
                        data-testid={`owner-pd-recommended-returning-${c.key}`}
                        title={`Pulled permits on ${c.builds} prior builds of this plan`}
                        style={{
                          fontFamily: '"Nunito Sans", sans-serif',
                          fontWeight: 600,
                          fontSize: 9.5,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "#3A4A35",
                          background: "#E8EAD9",
                          border: "1px solid #B8BFA0",
                          borderRadius: 3,
                          padding: "2px 7px",
                          lineHeight: 1.2,
                        }}
                      >
                        Returning
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                      color: "#6C6B85",
                      fontSize: 12,
                      marginTop: 2,
                      lineHeight: 1.4,
                    }}
                  >
                    License {c.license_no}
                  </div>
                  <div
                    className="tabular-nums mt-1"
                    style={{
                      fontSize: 11.5,
                      color: "#4F6149",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {c.builds} prior{" "}
                    {c.builds === 1 ? "build" : "builds"} of this plan
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div
        className="mt-4"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          fontSize: 11.5,
          color: "#9AA0C0",
          lineHeight: 1.5,
        }}
      >
        These are not platform endorsements — they are the firms whose
        names appear on the permit record for past builds of {designName}.
        Contact each directly to request a bid; share the design ID and
        the parcel address.
      </div>
    </div>
  );
}



function AccelaCard({ accela }) {
  const stateLabel = (accela.state || "—").replace(/_/g, " ");
  const syncLabel = (accela.sync_status || "").replace(/_/g, " ");
  const isSynced = (accela.sync_status || "").toLowerCase() === "synced";
  return (
    <div className="wn-card" style={{ padding: 18 }} data-testid="owner-pd-accela">
      <div className="flex items-start justify-between" style={{ gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="wn-label">City permit (Accela)</div>
          <div
            className="tabular-nums"
            style={{ color: "#3A5563", fontWeight: 700, fontSize: 18, marginTop: 4 }}
          >
            {accela.case_number || "—"}
          </div>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: "#1B1F4B",
              marginTop: 4,
            }}
          >
            {stateLabel}
          </div>
          {accela.submitted_at && (
            <div style={{ color: "#6C6B85", fontSize: 11.5, fontStyle: "italic", marginTop: 2 }}>
              Submitted {relativeDate(accela.submitted_at)}
            </div>
          )}
        </div>
        {syncLabel && (
          <span
            className="inline-flex items-center"
            data-testid="owner-pd-accela-sync"
            title={`Last city sync: ${syncLabel}`}
            style={{
              gap: 5,
              padding: "3px 9px",
              borderRadius: 999,
              background: isSynced ? "rgba(79,97,73,0.10)" : "rgba(154,160,192,0.18)",
              color: isSynced ? "#4F6149" : "#5B6196",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <RefreshCw size={10} strokeWidth={2.4} />
            {isSynced ? "Synced" : syncLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function ActivationPanel({ p, busy, onTrigger }) {
  const ap = p.activation_panel || {};
  const blocks = [
    {
      key: "811",
      title: "811 — Utility locate",
      icon: <Hash size={14} />,
      state: ap.locate_811,
      summary: (s) =>
        s?.ticket
          ? `Ticket ${s.ticket} · ${s.status}${s.expected_on_site ? ` · expected ${s.expected_on_site}` : ""}`
          : "Not requested",
    },
    {
      key: "aes",
      title: "AES — Electric service",
      icon: <Zap size={14} />,
      state: ap.aes,
      summary: (s) => (s?.service_no ? `${s.service_no} · ${s.status}` : "Not requested"),
    },
    {
      key: "citizens",
      title: "Citizens Energy — Gas / water",
      icon: <Flame size={14} />,
      state: ap.citizens_energy_gas || ap.citizens,
      summary: (s) => (s?.ref ? `${s.ref} · ${s.status}` : "Not requested"),
    },
    {
      key: "signage",
      title: "Neighborhood signage",
      icon: <Sparkles size={14} />,
      state: ap.signage,
      summary: (s) =>
        s?.ref
          ? `${s.ref} · ${s.status}${s.delivery_eta ? ` · ETA ${s.delivery_eta}` : ""}`
          : "Not requested",
    },
  ];
  return (
    <div className="wn-card" style={{ padding: 20 }} data-testid="owner-pd-activation">
      <h2
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 20,
          color: "#1B1F4B",
        }}
      >
        Build activations.
      </h2>
      <p style={{ color: "#6C6B85", fontSize: 12.5, marginTop: 2 }}>
        Trigger the standard utility and signage requests for your build. These integrations are
        simulated in this PoC and return realistic reference numbers immediately.
      </p>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {blocks.map((b) => {
          const requested = !!b.state;
          return (
            <div
              key={b.key}
              className="wn-card"
              style={{ padding: 14 }}
              data-testid={`activation-${b.key}`}
            >
              <div className="flex items-center gap-2" style={{ color: "#8F3C23" }}>
                {b.icon}
                <span
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#1B1F4B",
                  }}
                >
                  {b.title}
                </span>
              </div>
              <div
                className="tabular-nums"
                style={{ color: "#272636", fontSize: 12.5, marginTop: 6 }}
              >
                {b.summary(b.state)}
              </div>
              <button
                className="wn-btn wn-btn-tertiary mt-3"
                style={{ paddingLeft: 0 }}
                onClick={() => onTrigger(b.key)}
                disabled={busy === `act.${b.key}`}
                data-testid={`activation-trigger-${b.key}`}
              >
                {requested ? "Trigger again" : "Trigger"}{" "}
                <ChevronRight size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InspectionsCard({ p }) {
  const milestones = p.milestones || {};
  const order = ["foundation", "rough_in", "final_occupancy"];
  return (
    <div className="wn-card" style={{ padding: 20 }} data-testid="owner-pd-inspections">
      <h2
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 20,
          color: "#1B1F4B",
        }}
      >
        Inspection milestones.
      </h2>
      <p style={{ color: "#6C6B85", fontSize: 12.5, marginTop: 2 }}>
        Logged and verified by your architect of record.
      </p>
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        {order.map((k) => {
          const m = milestones[k];
          const status = m?.status || "Not Started";
          const tone =
            status === "Verified"
              ? { fg: "#4F6149", bg: "rgba(79,97,73,0.10)" }
              : status === "Visit Logged"
                ? { fg: "#8a651f", bg: "rgba(185,138,60,0.12)" }
                : { fg: "#5B6196", bg: "rgba(91,97,150,0.10)" };
          return (
            <div
              key={k}
              className="wn-card"
              style={{ padding: 14 }}
              data-testid={`owner-pd-milestone-${k}`}
            >
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 14,
                  color: "#1B1F4B",
                }}
              >
                {m?.name || k.replace(/_/g, " ")}
              </div>
              <div
                className="mt-2"
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: tone.bg,
                  color: tone.fg,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                {status}
              </div>
              {m?.verified_at && (
                <div
                  className="mt-2"
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    color: "#6C6B85",
                    fontSize: 11,
                  }}
                >
                  Verified {relativeDate(m.verified_at)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DocumentsCard({ p, busy, onGenerate, finalAvailable, viewDoc, setViewDoc }) {
  const { user } = useRole();
  const [packetBusy, setPacketBusy] = useState(false);
  const [packetErr, setPacketErr] = useState(null);
  const docs = p.documents || [];
  // Dedupe by doc_type — keep only the most recent generated copy per
  // type so 4 clicks on "Generate" don't create 4 visible duplicates.
  // Sort by generated_at desc, take the first occurrence per type.
  const dedupedDocs = (() => {
    const sorted = [...docs].sort(
      (a, b) => (b.generated_at || "").localeCompare(a.generated_at || ""),
    );
    const seen = new Set();
    const out = [];
    for (const d of sorted) {
      const key = d.doc_type || d.id;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(d);
    }
    return out;
  })();
  const existingTypes = new Set(docs.map((d) => d.doc_type));
  // The packet bundles any of these owner-side document types; we show
  // the "Send to lender" hero only once the owner has generated at
  // least one of them.
  const PACKET_DOC_TYPES = [
    "feasibility_report", "lender_underwriting",
    "ihcda_guidance", "rezoning_addendum", "final_record",
  ];
  const includedDocs = dedupedDocs.filter(
    (d) => PACKET_DOC_TYPES.includes(d.doc_type),
  );
  const hasCityMemo = !!(p.design?.city_review?.decided_at);
  const hasInspection = (p.inspections || []).length > 0;
  const packetReady = includedDocs.length > 0
    || hasCityMemo || hasInspection;

  const handlePacketDownload = async () => {
    if (!user?.id || !p?.id) return;
    setPacketBusy(true);
    setPacketErr(null);
    try {
      const slug = (p.design?.name || p.id).toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
      await downloadProjectPacket(p.id, user.id, `project-packet-${slug}.pdf`);
    } catch (e) {
      setPacketErr(e.message || "Download failed");
    } finally {
      setPacketBusy(false);
    }
  };

  return (
    <>
    <div className="wn-card" style={{ padding: 20 }} data-testid="owner-pd-documents">
      <h2
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 20,
          color: "#1B1F4B",
        }}
      >
        Documents.
      </h2>
      <p style={{ color: "#6C6B85", fontSize: 12.5, marginTop: 2 }}>
        Generate structured summaries pulled from your project data.
      </p>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {DOC_TYPES.map((d) => {
          const exists = existingTypes.has(d.key);
          const isBusy = busy === `doc.${d.key}`;
          return (
          <button
            key={d.key}
            className="wn-card text-left"
            style={{ padding: 14, cursor: "pointer" }}
            onClick={() => onGenerate(d.key)}
            disabled={isBusy}
            data-testid={`doc-generate-${d.key}`}
          >
            <div className="flex items-center gap-2" style={{ color: "#8F3C23" }}>
              <FileText size={14} />
              <span
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 14,
                  color: "#1B1F4B",
                }}
              >
                {d.label}
              </span>
            </div>
            <div className="wn-btn wn-btn-tertiary mt-2" style={{ paddingLeft: 0 }}>
              {isBusy ? "Generating…" : exists ? <>Regenerate <ChevronRight size={14} /></> : <>Generate <ChevronRight size={14} /></>}
            </div>
          </button>
          );
        })}

        <button
          className="wn-card text-left"
          style={{
            padding: 14,
            cursor: finalAvailable ? "pointer" : "not-allowed",
            opacity: finalAvailable ? 1 : 0.55,
          }}
          onClick={() => finalAvailable && onGenerate("final_record")}
          disabled={!finalAvailable || busy === "doc.final_record"}
          data-testid="doc-generate-final_record"
        >
          <div className="flex items-center gap-2" style={{ color: "#8F3C23" }}>
            <FileText size={14} />
            <span
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 14,
                color: "#1B1F4B",
              }}
            >
              Project Final Record
            </span>
          </div>
          <div
            className="wn-btn wn-btn-tertiary mt-2"
            style={{ paddingLeft: 0, color: finalAvailable ? "#1B1F4B" : "#9AA0C0" }}
          >
            {finalAvailable
              ? <>Generate <ChevronRight size={14} /></>
              : "Available after Construction Inspection"}
          </div>
        </button>
      </div>

      {dedupedDocs.length > 0 && (
        <div className="mt-5" style={{ borderTop: "1px solid #D9D8E4", paddingTop: 12 }}>
          <div className="wn-label">Generated</div>
          <div className="mt-2 space-y-1" data-testid="owner-pd-docs-list">
            {dedupedDocs.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setViewDoc(d)}
                className="flex items-center justify-between gap-3"
                data-testid={`owner-pd-doc-${d.id}`}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  border: "1px solid transparent",
                  borderRadius: 4,
                  padding: "10px 12px",
                  cursor: "pointer",
                  transition: "background-color 160ms ease, border-color 160ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F5F4FA";
                  e.currentTarget.style.borderColor = "#D9D8E4";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "transparent";
                }}
              >
                <div className="min-w-0 flex items-center" style={{ gap: 10 }}>
                  <FileText size={14} color="#8F3C23" />
                  <div className="min-w-0">
                    <div
                      style={{
                        fontFamily: '"Fraunces", Georgia, serif',
                        fontWeight: 600,
                        fontSize: 13.5,
                        color: "#1B1F4B",
                      }}
                    >
                      {d.label || d.doc_type}
                    </div>
                    <div
                      style={{
                        fontFamily: '"Newsreader", Georgia, serif',
                        fontStyle: "italic",
                        color: "#6C6B85",
                        fontSize: 11.5,
                      }}
                    >
                      {relativeDate(d.generated_at)}
                    </div>
                  </div>
                </div>
                <span
                  className="inline-flex items-center"
                  style={{
                    color: "#8F3C23",
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontWeight: 700,
                    fontSize: 12.5,
                    gap: 4,
                  }}
                >
                  View <ChevronRight size={12} />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Send-to-lender packet hero — shown once anything bundleable exists */}
      {packetReady && (
        <div
          className="mt-6"
          style={{
            borderTop: "1px solid #D9D8E4",
            paddingTop: 16,
          }}
          data-testid="owner-pd-packet"
        >
          <div
            className="flex items-start justify-between flex-wrap"
            style={{
              gap: 16,
              background: "#FFF6EA",
              border: "1px solid #E8C9A6",
              borderRadius: 4,
              padding: "16px 18px",
            }}
          >
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="wn-label" style={{ color: "#8F3C23" }}>
                Send to lender / IHCDA
              </div>
              <div
                className="mt-1"
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 500,
                  fontSize: 18,
                  color: "#1B1F4B",
                  lineHeight: 1.2,
                }}
              >
                Bundle everything into one branded packet.
              </div>
              <div
                className="mt-1"
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  color: "#6C6B85",
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                Cover letter + table of contents + {includedDocs.length}{" "}
                owner doc{includedDocs.length === 1 ? "" : "s"}
                {hasCityMemo && " + City decision memo"}
                {hasInspection && " + latest inspection report"}
                {" "}
                + joint signature block. One PDF.
              </div>
              {packetErr && (
                <div
                  className="mt-2"
                  style={{
                    color: "#B14E2F",
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    fontSize: 12.5,
                  }}
                  data-testid="owner-pd-packet-error"
                >
                  {packetErr}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handlePacketDownload}
              disabled={packetBusy}
              className="wn-btn wn-btn-primary inline-flex items-center"
              style={{ gap: 8, whiteSpace: "nowrap" }}
              data-testid="owner-pd-packet-download"
            >
              <Download size={14} />
              {packetBusy ? "Preparing packet…" : "Download packet"}
            </button>
          </div>
        </div>
      )}
    </div>
    {viewDoc && (
      <DocumentViewerModal
        doc={viewDoc}
        onClose={() => setViewDoc(null)}
      />
    )}
    </>
  );
}

function DocumentViewerModal({ doc, onClose }) {
  const { user } = useRole();
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState(null);
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, []);
  const handleDownload = async () => {
    if (!user?.id) return;
    setDownloading(true);
    setDownloadErr(null);
    try {
      const slug = (doc.label || doc.doc_type || "document")
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
      await downloadDocumentPdf(doc.id, user.id, `${slug}-${doc.id}.pdf`);
    } catch (e) {
      setDownloadErr(e.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };
  return ReactDOM.createPortal((
    <div
      role="dialog"
      aria-modal="true"
      data-testid="doc-viewer"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(27,31,75,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(760px, 100%)",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          background: "#FBFAFD",
          border: "1px solid #D9D8E4",
          borderRadius: 6,
          boxShadow: "0 28px 60px rgba(27,31,75,0.30)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "22px 28px 18px",
            borderBottom: "1px solid #D9D8E4",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div className="wn-label" style={{ color: "#8F3C23" }}>
              Generated · {relativeDate(doc.generated_at)}
            </div>
            <h2
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 500,
                fontSize: 26,
                color: "#1B1F4B",
                lineHeight: 1.18,
                margin: "4px 0 0",
              }}
              data-testid="doc-viewer-title"
            >
              {doc.label || doc.doc_type}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close document"
            data-testid="doc-viewer-close"
            style={{
              width: 32, height: 32, borderRadius: 4,
              background: "transparent",
              border: "1px solid #D9D8E4",
              color: "#1B1F4B",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: "22px 28px",
            overflowY: "auto",
            flex: 1,
          }}
          data-testid="doc-viewer-body"
        >
          {(doc.blocks || []).map((b, i) => (
            <section
              key={i}
              style={{
                paddingTop: i === 0 ? 0 : 18,
                paddingBottom: 18,
                borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
              }}
            >
              <div
                className="wn-label"
                style={{ color: "#6C6B85", marginBottom: 6 }}
              >
                {b.heading}
              </div>
              <div
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  color: "#272636",
                  fontSize: 15,
                  lineHeight: 1.6,
                  whiteSpace: "pre-line",
                }}
              >
                {b.body}
              </div>
            </section>
          ))}
          {(!doc.blocks || doc.blocks.length === 0) && (
            <div
              style={{
                color: "#6C6B85",
                fontStyle: "italic",
                fontSize: 14,
              }}
            >
              This document has no content yet.
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 28px 18px",
            borderTop: "1px solid #D9D8E4",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              type="button"
              onClick={onClose}
              className="wn-btn wn-btn-tertiary"
              data-testid="doc-viewer-cancel"
            >
              Close
            </button>
            {downloadErr && (
              <span
                style={{
                  color: "#B14E2F",
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  fontSize: 12.5,
                }}
                data-testid="doc-viewer-error"
              >
                {downloadErr}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="wn-btn wn-btn-primary inline-flex items-center"
            style={{ gap: 8 }}
            data-testid="doc-viewer-download"
          >
            <Download size={14} />
            {downloading ? "Preparing PDF…" : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

function HistoryCard({ p }) {
  const [tab, setTab] = useState("timeline");
  return (
    <div
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        padding: 0,
      }}
      data-testid="owner-pd-history"
    >
      {/* Header + tabs */}
      <div
        style={{
          padding: "20px 24px 0",
        }}
      >
        <div className="flex items-start justify-between" style={{ gap: 12 }}>
          <div>
            <div className="wn-label" style={{ color: "#8F3C23" }}>
              History
            </div>
            <h2
              className="mt-1"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 500,
                fontSize: 22,
                color: "#1B1F4B",
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              {tab === "timeline"
                ? "The story so far."
                : "Who did what, when."}
            </h2>
          </div>
        </div>
        <div
          className="mt-4 flex items-center"
          style={{ gap: 4, borderBottom: "1px solid #ECEBF1" }}
          role="tablist"
          aria-label="Project history view"
        >
          <HistoryTab
            id="timeline"
            label="Timeline"
            active={tab === "timeline"}
            onClick={() => setTab("timeline")}
          />
          <HistoryTab
            id="audit"
            label="Audit trail"
            active={tab === "audit"}
            onClick={() => setTab("audit")}
          />
        </div>
      </div>

      <div style={{ padding: "20px 24px 24px" }}>
        {tab === "timeline" && <ProjectTimeline p={p} />}
        {tab === "audit" && <ActivityList p={p} />}
      </div>
    </div>
  );
}

function HistoryTab({ id, label, active, onClick }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={`owner-pd-history-tab-${id}`}
      style={{
        position: "relative",
        background: "transparent",
        border: "none",
        padding: "10px 0",
        marginRight: 24,
        cursor: "pointer",
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: active ? 800 : 600,
        fontSize: 12.5,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: active ? "#1B1F4B" : "#6C6B85",
        transition: "color 160ms ease",
      }}
    >
      {label}
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: -1,
          height: 2,
          background: active ? "#B14E2F" : "transparent",
          transition: "background-color 200ms ease",
        }}
      />
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────
   ProjectTimeline — vertical narrative timeline. Walks
   STAGE_ORDER, pulls each stage's "entered at" date from
   p.stage_history, and renders a one-line description of what
   happened in each phase. Stages that haven't happened yet are
   rendered as quiet "Not started" rows so the owner can see the
   full project arc at a glance.
   ──────────────────────────────────────────────────────────────── */
function ProjectTimeline({ p }) {
  const stageIdx = STAGE_ORDER.indexOf(p.current_stage);
  const dateByStage = (p.stage_history || []).reduce((acc, h) => {
    if (h.stage && h.at) acc[h.stage] = h.at;
    return acc;
  }, {});

  // Per-stage human description, woven from project data.
  const designName = p.design?.name || "the design";
  const parcelAddr = p.parcel?.address || "the parcel";
  const firm = p.architect?.firm || "your architect";
  const planAmt = p.plan_purchase_receipt?.amount;
  const accelaRef = p.accela_ref?.case_number;
  const stageNote = (s) => {
    switch (s) {
      case "Selection":
        return `Project opened on ${parcelAddr} with ${designName}.`;
      case "Consultation":
        return `Consultation activated with ${firm}.`;
      case "Purchase":
        return planAmt
          ? `Construction plans purchased — $${Number(planAmt).toLocaleString()}.`
          : "Construction plans queued for purchase.";
      case "Expedited Review":
        return accelaRef
          ? `Permit submitted to the City — ${accelaRef}.`
          : "Submitted to the City for 10-day Expedited Review.";
      case "Build":
        return "Construction commenced on-site.";
      case "Construction Inspection":
        return "Inspections underway with the City — see breakdown below.";
      case "Complete":
        return "Project complete — final record on file with the City.";
      default:
        return s;
    }
  };

  return (
    <ol
      style={{ listStyle: "none", padding: 0, margin: 0, position: "relative" }}
      data-testid="owner-pd-timeline-list"
    >
      {/* Vertical rule running through the markers */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 11,
          top: 14,
          bottom: 14,
          width: 1,
          background: "#ECEBF1",
        }}
      />
      {STAGE_ORDER.map((s, i) => {
        const done = i < stageIdx;
        const current = i === stageIdx;
        const future = i > stageIdx;
        const at = dateByStage[s];
        const date = at
          ? new Date(at).toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : null;
        return (
          <li
            key={s}
            data-testid={`owner-pd-timeline-row-${s.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "24px 1fr auto",
              gap: 16,
              padding: "10px 0",
              alignItems: "start",
            }}
          >
            {/* Marker */}
            <span
              aria-hidden
              style={{
                width: 24,
                height: 24,
                borderRadius: 999,
                background: done
                  ? "#5B6196"
                  : current
                    ? "#FBFAFD"
                    : "#FBFAFD",
                border: done
                  ? "1px solid #5B6196"
                  : current
                    ? "2px solid #B14E2F"
                    : "1px solid #D9D8E4",
                color: done ? "#FBFAFD" : current ? "#B14E2F" : "#9AA0C0",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 800,
                fontSize: 10,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
                position: "relative",
                marginTop: 2,
              }}
            >
              {done ? (
                <Check size={12} strokeWidth={2.4} />
              ) : current ? (
                <Circle size={8} fill="#B14E2F" strokeWidth={0} />
              ) : (
                String(i + 1)
              )}
            </span>

            {/* Body */}
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 500,
                  fontSize: 16,
                  color: future ? "#9AA0C0" : "#1B1F4B",
                  lineHeight: 1.25,
                }}
              >
                {s}
                {current && s !== "Complete" && (
                  <span
                    className="wn-label"
                    style={{
                      marginLeft: 10,
                      color: "#8F3C23",
                      letterSpacing: "0.08em",
                      verticalAlign: "2px",
                    }}
                  >
                    In progress
                  </span>
                )}
                {current && s === "Complete" && (
                  <span
                    className="wn-label"
                    style={{
                      marginLeft: 10,
                      color: "#4F6149",
                      letterSpacing: "0.08em",
                      verticalAlign: "2px",
                    }}
                  >
                    Done
                  </span>
                )}
              </div>
              {!future ? (
                <div
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    color: "#272636",
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    marginTop: 2,
                    maxWidth: 560,
                  }}
                >
                  {stageNote(s)}
                </div>
              ) : (
                <div
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    color: "#9AA0C0",
                    fontSize: 13,
                    lineHeight: 1.45,
                    marginTop: 2,
                  }}
                >
                  Not started yet.
                </div>
              )}
              {s === "Construction Inspection" && (done || current) && (
                <InspectionSubTimeline milestones={p.milestones || {}} />
              )}
            </div>

            {/* Date */}
            <div
              className="tabular-nums"
              style={{
                textAlign: "right",
                whiteSpace: "nowrap",
                color: current ? "#8F3C23" : future ? "#9AA0C0" : "#6C6B85",
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                fontSize: 12.5,
                marginTop: 4,
              }}
            >
              {date
                ? (current
                    ? (s === "Complete" ? `completed ${date}` : `entered ${date}`)
                    : date)
                : future
                  ? "—"
                  : "—"}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ────────────────────────────────────────────────────────────────
   InspectionSubTimeline — nested sub-list rendered under the
   "Construction Inspection" row of ProjectTimeline. Shows the
   three required inspections for the City of Indianapolis flow:
   Foundation, Framing & Rough-In, and Final. Status is read
   from p.milestones (foundation / rough_in / final_occupancy);
   "Verified" => done, "Visit Logged" => in progress, anything
   else (incl. "Not Started" or missing) => not started yet.
   ──────────────────────────────────────────────────────────────── */
const INSPECTION_SUB_STEPS = [
  { key: "foundation", label: "Foundation Inspection" },
  { key: "rough_in", label: "Framing and Rough-In Inspection" },
  { key: "final_occupancy", label: "Final Inspection" },
];

function InspectionSubTimeline({ milestones }) {
  const subStatus = (m) => {
    const s = (m || {}).status;
    if (s === "Verified") return "done";
    if (s === "Visit Logged") return "current";
    return "future";
  };
  const subDate = (m) => {
    const at = (m || {}).verified_at || (m || {}).last_visit_at;
    if (!at) return null;
    try {
      return new Date(at).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return null;
    }
  };

  return (
    <ol
      data-testid="owner-pd-timeline-inspections-sublist"
      style={{
        listStyle: "none",
        padding: 0,
        margin: "10px 0 2px 0",
        position: "relative",
        borderLeft: "1px solid #ECEBF1",
        paddingLeft: 14,
      }}
    >
      {INSPECTION_SUB_STEPS.map(({ key, label }) => {
        const m = milestones[key];
        const state = subStatus(m);
        const done = state === "done";
        const current = state === "current";
        const future = state === "future";
        const date = subDate(m);
        return (
          <li
            key={key}
            data-testid={`owner-pd-timeline-inspection-${key}`}
            style={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "14px 1fr auto",
              gap: 12,
              padding: "5px 0",
              alignItems: "center",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                background: done
                  ? "#5B6196"
                  : current
                    ? "#FBFAFD"
                    : "#FBFAFD",
                border: done
                  ? "1px solid #5B6196"
                  : current
                    ? "2px solid #B14E2F"
                    : "1px solid #D9D8E4",
                marginLeft: 2,
              }}
            />
            <div style={{ minWidth: 0 }}>
              <span
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontSize: 13.5,
                  color: future ? "#9AA0C0" : "#272636",
                  fontStyle: future ? "italic" : "normal",
                  lineHeight: 1.4,
                }}
              >
                {label}
              </span>
              {current && (
                <span
                  className="wn-label"
                  style={{
                    marginLeft: 8,
                    color: "#8F3C23",
                    letterSpacing: "0.08em",
                    fontSize: 9.5,
                  }}
                >
                  In progress
                </span>
              )}
              {done && (
                <span
                  className="wn-label"
                  style={{
                    marginLeft: 8,
                    color: "#4F6149",
                    letterSpacing: "0.08em",
                    fontSize: 9.5,
                  }}
                >
                  Verified
                </span>
              )}
            </div>
            <div
              className="tabular-nums"
              style={{
                textAlign: "right",
                whiteSpace: "nowrap",
                color: current ? "#8F3C23" : future ? "#9AA0C0" : "#6C6B85",
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                fontSize: 11.5,
              }}
            >
              {date || (future ? "—" : "—")}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ActivityCard({ p }) {
  // Outer card chrome wrapper around ActivityList (kept for legacy use).
  return (
    <div
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        padding: 24,
      }}
      data-testid="owner-pd-activity"
    >
      <div className="wn-label" style={{ color: "#8F3C23" }}>
        Project audit trail
      </div>
      <h2
        className="mt-1"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 22,
          color: "#1B1F4B",
          margin: 0,
          lineHeight: 1.2,
        }}
      >
        Who did what, when.
      </h2>
      <div className="mt-5">
        <ActivityList p={p} />
      </div>
    </div>
  );
}

function ActivityList({ p }) {
  const [resolved, setResolved] = useState(null);
  useEffect(() => {
    if (!p?.id || !p?.owner_id) return;
    api
      .get(
        `/owner-portal/activity/resolve?user_id=${p.owner_id}&project_id=${p.id}&limit=40`,
      )
      .then((r) => setResolved(r.data.items || []))
      .catch(() => setResolved([]));
  }, [p?.id, p?.owner_id]);

  const fallback = p.activity || [];
  const rawItems = resolved !== null ? resolved : fallback;
  if (rawItems.length === 0) {
    return (
      <div
        data-testid="owner-pd-activity-empty"
        style={{
          color: "#6C6B85",
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          fontSize: 14,
        }}
      >
        No audit entries recorded yet.
      </div>
    );
  }

  // Collapse near-identical consecutive entries (e.g. "plans_purchased"
  // and "plans.purchased" emitted by the same actor on the same record
  // — sometimes with record_type "projects" vs "project") into a single
  // row + a "+N similar" note. Same pattern as the Workspace audit feed.
  const items = rawItems.reduce((acc, a) => {
    const last = acc[acc.length - 1];
    const sameActor = last && last.actor_id === a.actor_id;
    const sameRecord = last && last.record_id === a.record_id;
    const sameStem = last
      && normaliseAction(last.action) === normaliseAction(a.action);
    if (last && sameActor && sameRecord && sameStem) {
      last.dedup_count = (last.dedup_count || 1) + 1;
      return acc;
    }
    acc.push({ ...a, dedup_count: 1 });
    return acc;
  }, []);

  // Group by ISO date (YYYY-MM-DD)
  const groups = items.reduce((acc, a) => {
    const day = (a.at || "").slice(0, 10) || "Earlier";
    (acc[day] = acc[day] || []).push(a);
    return acc;
  }, {});
  const days = Object.keys(groups).sort().reverse();

  return (
    <div data-testid="owner-pd-activity-list">
      <div
        className="tabular-nums wn-label"
        style={{
          color: "#6C6B85",
          marginBottom: 12,
        }}
      >
        {items.length} {items.length === 1 ? "entry" : "entries"}
      </div>
      {days.map((day, di) => (
        <div key={day} style={{ marginTop: di === 0 ? 0 : 24 }}>
          <div
            className="tabular-nums wn-label"
            style={{
              color: "#272636",
              paddingBottom: 8,
              borderBottom: "1px solid #ECEBF1",
              marginBottom: 4,
            }}
          >
            {dayLabel(day)}
          </div>
          {groups[day].map((a, i) => (
            <ActivityRow key={(a.id || a.at) + "-" + i} a={a} />
          ))}
        </div>
      ))}
    </div>
  );
}

function ActivityRow({ a }) {
  const actor = a.actor || {};
  const name = actor.name || "Platform";
  const initials = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  const tone = ACTOR_TONE[actor.role] || ACTOR_TONE.default;
  const time = a.at ? new Date(a.at).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }) : "—";
  return (
    <div
      className="grid items-start"
      style={{
        gridTemplateColumns: "32px 1fr 80px",
        gap: 12,
        padding: "10px 0",
      }}
      data-testid={`activity-row-${a.action || "x"}`}
    >
      <span
        aria-hidden
        style={{
          width: 32,
          height: 32,
          borderRadius: 999,
          background: tone.bg,
          color: tone.fg,
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 700,
          fontSize: 11,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          letterSpacing: "0.04em",
        }}
        title={actor.role || "actor"}
      >
        {initials}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, color: "#272636", lineHeight: 1.45 }}>
          <span style={{ color: "#1B1F4B", fontWeight: 700 }}>{name}</span>
          {actor.organization && (
            <span style={{ color: "#6C6B85" }}> · {actor.organization}</span>
          )}
          {" "}
          <span style={{ color: "#272636" }}>{humanAction(a)}</span>
          {a.dedup_count > 1 && (
            <span
              style={{
                color: "#6C6B85",
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                marginLeft: 6,
              }}
            >
              · +{a.dedup_count - 1} similar
            </span>
          )}
        </div>
        {a.record_type && a.record_id && (
          <div
            className="tabular-nums"
            style={{
              color: "#6C6B85",
              fontSize: 11.5,
              marginTop: 2,
              fontFamily: '"Nunito Sans", sans-serif',
              letterSpacing: "0.02em",
            }}
          >
            {a.record_type}/{a.record_id}
          </div>
        )}
      </div>
      <span
        className="tabular-nums"
        style={{
          textAlign: "right",
          color: "#6C6B85",
          fontSize: 12,
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
        }}
        title={a.at}
      >
        {time}
      </span>
    </div>
  );
}

const ACTOR_TONE = {
  owner_builder: { bg: "#8F3C23", fg: "#FBFAFD" },
  architect:     { bg: "#1B1F4B", fg: "#FBFAFD" },
  city_staff:    { bg: "#3A5563", fg: "#FBFAFD" },
  platform_admin:{ bg: "#4F6149", fg: "#FBFAFD" },
  system:        { bg: "#D9D8E4", fg: "#1B1F4B" },
  default:       { bg: "#272636", fg: "#FBFAFD" },
};

function dayLabel(day) {
  if (!day || day === "Earlier") return "Earlier";
  try {
    const d = new Date(day + "T12:00:00Z");
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) return "Today";
    const yest = new Date(today);
    yest.setDate(today.getDate() - 1);
    if (d.toDateString() === yest.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return day;
  }
}

// ─── Purchase modal styling helpers ───
const modalInputStyle = {
  display: "block",
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #D9D8E4",
  borderRadius: 4,
  background: "#FBFAFD",
  fontFamily: '"Nunito Sans", sans-serif',
  color: "#1B1F4B",
  fontSize: 14,
  outline: "none",
  transition: "border-color 140ms ease",
};

const modalBackBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "9px 14px",
  borderRadius: 4,
  border: "1px solid #D9D8E4",
  background: "transparent",
  color: "#6C6B85",
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
};

function ModalField({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <span
        className="wn-label"
        style={{ color: "#6C6B85", display: "block", marginBottom: 6 }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function humanAction(a) {
  if (a.action === "plans_purchased") return `Plans purchased${a.note ? ` (${a.note})` : ""}.`;
  if (a.action === "document_generated") return `Generated ${a.note || "a document"}.`;
  if (a.action && a.action.startsWith("activation.")) return a.note || a.action;
  if (a.action === "inspection.logged") return `Inspection visit logged${a.note ? ` — ${a.note}` : ""}.`;
  if (a.action === "milestone_verified") return `Milestone verified${a.note ? ` — ${a.note}` : ""}.`;
  if (a.action === "signed_to_dbns") return `Signed and submitted to DBNS.`;
  return `${a.action || "activity"}${a.note ? ` — ${a.note}` : ""}`;
}

// Treat "plans_purchased" and "plans.purchased" (and similar dotted/
// underscored variants) as the same logical action for dedup purposes.
function normaliseAction(action) {
  if (!action) return "";
  return String(action).toLowerCase().replace(/[._-]/g, "");
}

function DesignSummary({ p }) {
  const d = p.design;
  if (!d) return null;
  return (
    <div className="wn-card" style={{ padding: 16 }}>
      <div
        style={{
          width: "100%",
          height: 130,
          backgroundImage: `url(${assetUrl(d.exterior_photo)})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: 4,
        }}
        aria-hidden
      />
      <div
        style={{
          fontSize: 11.5,
          color: "#8F3C23",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginTop: 8,
        }}
      >
        {formatTypology(d.typology)}
      </div>
      <div
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 17,
          color: "#1B1F4B",
        }}
      >
        {d.name}
      </div>
      <Link
        to={`/marketplace/${d.id}`}
        className="wn-btn wn-btn-tertiary mt-2"
        style={{ paddingLeft: 0 }}
      >
        View design <ChevronRight size={14} />
      </Link>
    </div>
  );
}

function ParcelSummary({ p }) {
  const pp = p.parcel;
  if (!pp) return null;
  return (
    <div className="wn-card" style={{ padding: 16 }}>
      <div className="wn-label">Parcel</div>
      <div
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 15,
          color: "#1B1F4B",
          marginTop: 4,
        }}
      >
        {pp.address}
      </div>
      <div className="tabular-nums" style={{ color: "#6C6B85", fontSize: 12 }}>
        {pp.width_ft}′ × {pp.depth_ft}′ · {pp.zoning} · {pp.neighborhood}
      </div>
      <Link
        to={`/parcels/${pp.id}`}
        className="wn-btn wn-btn-tertiary mt-2"
        style={{ paddingLeft: 0 }}
      >
        View parcel <ChevronRight size={14} />
      </Link>
    </div>
  );
}

function CostsCard({ p }) {
  const c = p.costs || {};
  const stage = (p.current_stage || "").toLowerCase();
  const sqft = (p.design && p.design.sqft) || 0;
  const planAmount = 2500 + Math.max(0, parseInt(sqft, 10) || 0);
  const planPaid = !!p.plan_purchase_receipt;
  const investedToDate = planPaid
    ? (p.plan_purchase_receipt?.amount || planAmount)
    : 0;
  const isPurchase = stage === "purchase" && !planPaid;
  return (
    <div className="wn-card" style={{ padding: 18 }} data-testid="owner-pd-costs">
      <div className="wn-label" style={{ color: "#8F3C23" }}>
        Investment timeline
      </div>
      <h3
        className="mt-1"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 18,
          color: "#1B1F4B",
          margin: 0,
          lineHeight: 1.2,
        }}
      >
        {formatPriceFull(c.total_cost || 0)} committed
      </h3>
      <div
        className="mt-1"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 13,
          lineHeight: 1.4,
        }}
      >
        Hard {formatPriceFull(c.hard_cost || 0)} · Soft {formatPriceFull(c.soft_costs || 0)}
      </div>

      {/* Progress: spent vs committed */}
      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <span className="wn-label" style={{ color: "#6C6B85" }}>
            Spent to date
          </span>
          <span
            className="tabular-nums"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 13,
              color: "#1B1F4B",
            }}
          >
            {formatPriceFull(investedToDate)}
          </span>
        </div>
        <div
          aria-hidden
          className="mt-1"
          style={{
            height: 4,
            borderRadius: 999,
            background: "#ECEBF1",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(100, (investedToDate / Math.max(1, c.total_cost || 1)) * 100)}%`,
              background: "#4F6149",
              transition: "width 360ms ease",
            }}
          />
        </div>
      </div>

      {/* Next charge */}
      {isPurchase && (
        <div
          className="mt-4"
          style={{
            borderTop: "1px solid #ECEBF1",
            paddingTop: 12,
          }}
          data-testid="owner-pd-next-charge"
        >
          <div className="flex items-baseline justify-between">
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              Next charge
            </span>
            <span
              className="tabular-nums"
              style={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 800,
                fontSize: 15,
                color: "#8F3C23",
              }}
            >
              {formatPriceFull(planAmount)}
            </span>
          </div>
          <div
            style={{
              color: "#6C6B85",
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              fontSize: 12.5,
              lineHeight: 1.45,
              marginTop: 2,
            }}
          >
            Construction plans · unlocks Expedited Review
          </div>
        </div>
      )}

      {/* Coming up after plan purchase */}
      {!isPurchase && (
        <div
          className="mt-4 tabular-nums"
          style={{
            borderTop: "1px solid #ECEBF1",
            paddingTop: 12,
            display: "grid",
            gridTemplateColumns: "1fr auto",
            rowGap: 4,
            columnGap: 8,
            fontSize: 12.5,
            color: "#272636",
          }}
        >
          <span style={{ color: "#6C6B85" }}>Hard cost</span>
          <span>{formatPriceFull(c.hard_cost || 0)}</span>
          <span style={{ color: "#6C6B85" }}>Soft costs</span>
          <span>{formatPriceFull(c.soft_costs || 0)}</span>
          <span style={{ color: "#6C6B85", fontWeight: 700 }}>Total</span>
          <span style={{ color: "#8F3C23", fontWeight: 700 }}>
            {formatPriceFull(c.total_cost || 0)}
          </span>
        </div>
      )}
    </div>
  );
}
